import { Api } from './api';
import { el, svgIcon, ICONS } from './dom';
import { EventBatcher } from './events';
import { Identity } from './identity';
import { ApiError, EligibleSurvey, SurveyAnswer } from './types';
import { renderQuestion } from './question-render';
import { dlog, hasFinePointer, pageUrl, throttle } from './util';

// Survey trigger engine + renderer (docs/widget.md §2, §4). The SERVER decides
// eligibility (active, schedule, url match, answered-before); the widget only
// detects the trigger moment, renders, and reports shown/dismissed/answers.

const STRINGS = {
  fa: {
    submit: 'ثبت پاسخ',
    close: 'بستن',
    dismiss: 'بستن نظرسنجی',
    thanks: 'ممنون از پاسخ شما',
    requiredErr: 'لطفاً به سؤال‌های الزامی (*) پاسخ دهید',
    otherPlaceholder: 'توضیح دهید…',
    textPlaceholder: 'پاسخ خود را بنویسید…',
    followPlaceholder: 'نظرتان را بنویسید…',
    optional: '(اختیاری)',
    other: 'سایر',
    npsLow: 'اصلاً پیشنهاد نمی‌کنم',
    npsHigh: 'حتماً پیشنهاد می‌کنم',
    submitErr: 'ثبت پاسخ ناموفق بود؛ دوباره تلاش کنید',
    choose: 'انتخاب کنید',
    yes: 'بله',
    datePick: 'انتخاب تاریخ',
    today: 'امروز',
    clear: 'پاک کردن',
    prevMonth: 'ماه قبل',
    nextMonth: 'ماه بعد',
  },
  en: {
    submit: 'Submit',
    close: 'Close',
    dismiss: 'Dismiss survey',
    thanks: 'Thanks for your feedback',
    requiredErr: 'Please answer the required (*) questions',
    otherPlaceholder: 'Please specify…',
    textPlaceholder: 'Write your answer…',
    followPlaceholder: 'Tell us more…',
    optional: '(optional)',
    other: 'Other',
    npsLow: 'Not at all likely',
    npsHigh: 'Extremely likely',
    submitErr: 'Submitting failed; please try again',
    choose: 'Select…',
    yes: 'Yes',
    datePick: 'Select date',
    today: 'Today',
    clear: 'Clear',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
  },
};

type Disarm = () => void;

export class SurveyEngine {
  private disarms: Disarm[] = [];
  private shownThisPageView = false;
  private card: HTMLElement | undefined;
  private backdrop: HTMLElement | undefined;
  private readonly t: (typeof STRINGS)['fa'];

  constructor(
    private readonly host: HTMLElement, // .cb-root inside the shadow
    private readonly api: Api,
    private readonly ids: Identity,
    private readonly events: EventBatcher,
    private readonly language: string,
    private readonly position: 'bottom-right' | 'bottom-left',
  ) {
    this.t = language === 'fa' ? STRINGS.fa : STRINGS.en;
  }

  /** called on boot and on every SPA route change — re-fetch, re-arm */
  async refresh(): Promise<void> {
    this.disarmAll();
    this.shownThisPageView = false;
    let surveys: EligibleSurvey[] = [];
    try {
      const anonId = this.ids.getAnonId();
      const query = new URLSearchParams();
      if (anonId) query.set('anonId', anonId);
      query.set('pageUrl', pageUrl());
      surveys = await this.api.get<EligibleSurvey[]>(`/public/surveys/eligible?${query}`);
    } catch (err) {
      dlog('eligible fetch failed', err);
      return;
    }
    for (const survey of surveys) {
      if (!this.ids.isSurveySuppressed(survey.surveyId)) this.arm(survey);
    }
    dlog('surveys armed', surveys.length);
  }

  private disarmAll(): void {
    for (const disarm of this.disarms.splice(0)) {
      try {
        disarm();
      } catch {
        /* ignore */
      }
    }
  }

  // ---------- triggers ----------

  private arm(survey: EligibleSurvey): void {
    const fire = () => this.fire(survey);
    const conditions = survey.trigger.conditions ?? {};

    switch (survey.trigger.type) {
      case 'page_visit':
        fire();
        break;

      case 'time_on_page': {
        const timer = setTimeout(fire, (conditions.seconds ?? 10) * 1000);
        this.disarms.push(() => clearTimeout(timer));
        break;
      }

      case 'scroll_depth': {
        const target = conditions.scrollPercent ?? 50;
        const onScroll = throttle(() => {
          const doc = document.documentElement;
          const scrollable = doc.scrollHeight - window.innerHeight;
          const percent = scrollable <= 0 ? 100 : (window.scrollY / scrollable) * 100;
          if (percent >= target) fire();
        }, 250);
        window.addEventListener('scroll', onScroll, { passive: true });
        this.disarms.push(() => window.removeEventListener('scroll', onScroll));
        break;
      }

      case 'exit_intent': {
        if (!hasFinePointer()) break; // desktop-only signal
        const onOut = (ev: MouseEvent) => {
          if (!ev.relatedTarget && ev.clientY <= 0) {
            this.events.track('exit_intent', { surveyId: survey.surveyId });
            fire();
          }
        };
        document.addEventListener('mouseout', onOut);
        this.disarms.push(() => document.removeEventListener('mouseout', onOut));
        break;
      }

      case 'tab_close': {
        // A page can't detect the tab's × click or render during the close — the
        // tab bar is browser chrome, outside the page sandbox. The closest we get:
        //  • desktop — a real close/reload/navigation hits `beforeunload`, where the
        //    browser shows its native "Leave site?" dialog. If the visitor CANCELS
        //    (stays), the page keeps running, so a short timer that survives the
        //    (modal, JS-blocking) dialog fires the survey; on "Leave" the page
        //    unloads first and the timer never runs. Needs prior page interaction
        //    (browser rule) and also triggers on reload/navigation.
        //  • mobile — browsers ignore beforeunload dialogs; fall back to
        //    visibilitychange → hidden (queued, seen when the visitor returns).
        if (hasFinePointer()) {
          let leaving = false;
          const onBeforeUnload = (ev: BeforeUnloadEvent) => {
            ev.preventDefault();
            ev.returnValue = ''; // required for the native dialog in Chrome/Firefox
            leaving = true;
            // runs only if the page is still alive after the dialog → they stayed
            setTimeout(() => {
              if (leaving) {
                leaving = false;
                fire();
              }
            }, 1000);
          };
          window.addEventListener('beforeunload', onBeforeUnload);
          this.disarms.push(() => window.removeEventListener('beforeunload', onBeforeUnload));
        } else {
          const onHide = () => {
            if (document.visibilityState === 'hidden') fire();
          };
          document.addEventListener('visibilitychange', onHide);
          this.disarms.push(() => document.removeEventListener('visibilitychange', onHide));
        }
        break;
      }

      default:
        // 'manual' — fired only through window.ChatBI.showSurvey (no arming)
        break;
    }
  }

  private fire(survey: EligibleSurvey): void {
    if (this.shownThisPageView) return; // at most one survey per page view
    this.shownThisPageView = true;
    this.disarmAll();
    const delay = survey.display?.delayMs ?? 0;
    setTimeout(() => this.show(survey), delay);
  }

  // ---------- rendering ----------

  private show(survey: EligibleSurvey): void {
    if (this.card) return;
    const style = survey.display?.style === 'slide_in' ? 'slide_in' : 'popup';

    if (style === 'popup') {
      this.backdrop = el('div', {
        class: 'cb-backdrop',
        on: { click: () => this.dismiss(survey) },
      });
      this.host.appendChild(this.backdrop);
    }

    const sideStyle =
      style === 'slide_in'
        ? this.position === 'bottom-left'
          ? 'right: var(--cb-margin-x);' // opposite corner from the launcher
          : 'left: var(--cb-margin-x);'
        : '';

    const answers = new Map<string, SurveyAnswer>();
    const form = el('div');
    for (const question of survey.questions) form.appendChild(renderQuestion(question, answers, this.t));

    const errEl = el('div', { class: 'cb-formerr' });
    const submitBtn = el('button', {
      class: 'cb-submit',
      text: this.t.submit,
      attrs: { type: 'button' },
      on: { click: () => void this.submit(survey, answers, errEl, submitBtn) },
    });
    const closeBtn = el('button', {
      class: 'cb-ghost',
      text: this.t.close,
      attrs: { type: 'button' },
      on: { click: () => this.dismiss(survey) },
    });
    const actions = el('div', { class: 'cb-actions' }, [submitBtn, closeBtn]);

    this.card = el(
      'div',
      {
        class: `cb-survey ${style}`,
        attrs: {
          role: 'dialog',
          'aria-modal': style === 'popup' ? 'true' : 'false',
          style: sideStyle,
        },
        on: {
          keydown: (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') this.dismiss(survey);
          },
        },
      },
      [
        el(
          'button',
          {
            class: 'cb-iconbtn cb-x',
            attrs: { 'aria-label': this.t.dismiss, type: 'button' },
            on: { click: () => this.dismiss(survey) },
          },
          [svgIcon(ICONS.close, 16)],
        ),
        form,
        errEl,
        actions,
      ],
    );
    this.host.appendChild(this.card);
    (this.card.querySelector('button:not(.cb-x)') as HTMLElement | null)?.focus();

    // server owns the shown counter + survey_shown event
    void this.api
      .post(`/public/surveys/${survey.surveyId}/track`, {
        action: 'shown',
        anonId: this.ids.getAnonId(),
        pageUrl: pageUrl(),
      })
      .catch((err) => dlog('track shown failed', err));
  }

  private dismiss(survey: EligibleSurvey): void {
    this.removeCard();
    this.ids.suppressSurvey(survey.surveyId);
    void this.api
      .post(`/public/surveys/${survey.surveyId}/track`, {
        action: 'dismissed',
        anonId: this.ids.getAnonId(),
        pageUrl: pageUrl(),
      })
      .catch((err) => dlog('track dismissed failed', err));
  }

  private removeCard(): void {
    this.card?.remove();
    this.backdrop?.remove();
    this.card = undefined;
    this.backdrop = undefined;
  }

  private async submit(
    survey: EligibleSurvey,
    answers: Map<string, SurveyAnswer>,
    errEl: HTMLElement,
    submitBtn: HTMLButtonElement,
  ): Promise<void> {
    const missing = survey.questions.some((q) => q.required && !answers.has(q.qid));
    if (missing || answers.size === 0) {
      errEl.textContent = this.t.requiredErr;
      return;
    }
    const anonId = this.ids.getAnonId();
    if (!anonId) return; // no identity → nothing to attribute; stay silent

    errEl.textContent = '';
    submitBtn.disabled = true;
    try {
      await this.api.post(`/public/surveys/${survey.surveyId}/responses`, {
        anonId,
        pageUrl: pageUrl(),
        answers: Array.from(answers.values()),
      });
      this.ids.suppressSurvey(survey.surveyId);
      this.showThanks();
    } catch (err) {
      const e = err as ApiError;
      dlog('survey submit failed', e);
      errEl.textContent = (this.language === 'fa' ? e.persianMessage : e.message) || this.t.submitErr;
      submitBtn.disabled = false;
    }
  }

  private showThanks(): void {
    if (!this.card) return;
    this.card.textContent = '';
    this.card.appendChild(
      el('div', { class: 'cb-thanks' }, [
        el('div', { class: 'cb-thanksicon' }, [svgIcon(ICONS.check, 24)]),
        el('div', { class: 'cb-thankstext', text: this.t.thanks }),
      ]),
    );
    setTimeout(() => this.removeCard(), 2500);
  }
}
